import { getApiEndpoint } from '@/config/api';

/**
 * Função auxiliar para obter o token de autenticação do localStorage
 */
const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

/**
 * Função auxiliar para criar headers com autenticação
 */
const getAuthHeaders = (): HeadersInit => {
  const token = getAuthToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

export interface ContactMessage {
  id: number;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  is_read: boolean;
  read_at?: string;
  read_by?: number;
  created_at: string;
  updated_at: string;
}

export interface CreateContactMessage {
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
}

export class ContactService {
  /**
   * Envia uma mensagem de contato
   */
  static async sendContactMessage(data: CreateContactMessage): Promise<number> {
    try {
      const response = await fetch(getApiEndpoint('api/contacts'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} | body: ${errorText}`);
      }
      
      const result = await response.json();
      return result.id;
    } catch (error) {
      console.error('Erro ao enviar mensagem de contato:', error);
      throw error;
    }
  }

  /**
   * Busca todas as mensagens de contato (apenas admin)
   */
  static async getAllContacts(): Promise<ContactMessage[]> {
    try {
      const response = await fetch(getApiEndpoint('api/contacts/admin'), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const contacts = await response.json();
      return contacts as ContactMessage[];
    } catch (error) {
      console.error('Erro ao buscar contatos:', error);
      throw error;
    }
  }

  /**
   * Busca uma mensagem de contato específica por ID
   */
  static async getContactById(id: number): Promise<ContactMessage | null> {
    try {
      const response = await fetch(getApiEndpoint(`api/contacts/${id}`), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      
      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const contact = await response.json();
      return contact as ContactMessage;
    } catch (error) {
      console.error('Erro ao buscar contato:', error);
      throw error;
    }
  }

  /**
   * Marca uma mensagem como lida/não lida
   */
  static async toggleReadStatus(id: number, isRead: boolean): Promise<boolean> {
    try {
      const response = await fetch(getApiEndpoint(`api/contacts/${id}/read-status`), {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_read: isRead }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return true;
    } catch (error) {
      console.error('Erro ao atualizar status de leitura:', error);
      throw error;
    }
  }

  /**
   * Exclui uma mensagem de contato
   */
  static async deleteContact(id: number): Promise<boolean> {
    try {
      const response = await fetch(getApiEndpoint(`api/contacts/${id}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return true;
    } catch (error) {
      console.error('Erro ao excluir contato:', error);
      throw error;
    }
  }
}

